import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import APICall, { GetUploadMediaBaseLocation } from "../../Utility/APICall";
import { paths, cloudStorageBucket } from "../../Utility/Constants";
import { useAuth } from "../../Utility/AuthProvider";
import { useUser } from "../../Utility/UserProvider";
import FileUpload from "../../Utility/FileUpload"; // ENSURE: input type="file" multiple; onFileUpload(event.target.files)
import FileDownload from "../../Utility/FileDownload";

// React Icons
import { FaPlusCircle, FaMinusCircle, FaTrashAlt } from "react-icons/fa"; // Added FaTrashAlt

// Firebase imports
import { storage } from "../../Utility/firebase";
import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
  deleteObject,
} from "firebase/storage"; // Added deleteObject

// --- Reusable Form Field Components (Keep these as they are) ---
const FormInput = ({
  label,
  id,
  name,
  placeholder,
  value,
  onChange,
  type = "text",
  error,
  required = false,
}) => (
  <div>
    {" "}
    <label
      htmlFor={id}
      className="block text-sm font-medium text-gray-700 mb-1"
    >
      {" "}
      {label} {required && <span className="text-red-500">*</span>}{" "}
    </label>{" "}
    <input
      type={type}
      id={id}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${error ? "border-red-500" : ""}`}
    />{" "}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}{" "}
  </div>
);
const FormSelect = ({
  label,
  id,
  name,
  value,
  onChange,
  children,
  error,
  required = false,
}) => (
  <div>
    {" "}
    <label
      htmlFor={id}
      className="block text-sm font-medium text-gray-700 mb-1"
    >
      {" "}
      {label} {required && <span className="text-red-500">*</span>}{" "}
    </label>{" "}
    <select
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className={`block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${error ? "border-red-500" : ""}`}
    >
      {" "}
      {children}{" "}
    </select>{" "}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}{" "}
  </div>
);
const NumberStepper = ({
  label,
  value,
  onIncrement,
  onDecrement,
  min = 0,
  name,
  onChange,
}) => (
  <div className="flex justify-between items-center py-3 border-b border-gray-200 last:border-b-0">
    {" "}
    <p className="text-sm text-gray-700">{label}</p>{" "}
    <div className="flex items-center gap-2 sm:gap-3">
      {" "}
      <button
        type="button"
        onClick={onDecrement}
        disabled={value <= min}
        className="p-1.5 rounded-full text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label={`Decrement ${label}`}
      >
        {" "}
        <FaMinusCircle className="w-5 h-5" />{" "}
      </button>{" "}
      <input
        type="number"
        name={name}
        value={value}
        onChange={onChange}
        min={min}
        className="w-12 text-center border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
      />{" "}
      <button
        type="button"
        onClick={onIncrement}
        className="p-1.5 rounded-full text-gray-500 hover:bg-gray-200"
        aria-label={`Increment ${label}`}
      >
        {" "}
        <FaPlusCircle className="w-5 h-5" />{" "}
      </button>{" "}
    </div>{" "}
  </div>
);
const amenitiesList = [
  "WiFi",
  "CCTV",
  "Television",
  "Parking",
  "Refrigerator",
  "Laundry",
  "Security",
  "Inverter",
  "Power Backup",
  "Lift",
  "Gym",
  "Pool",
];
const furnishingItems = [
  { label: "Select Furnishing", value: "" },
  { label: "Furnished", value: "Furnished" },
  { label: "Semi-Furnished", value: "Semi-furnished" },
  { label: "Unfurnished", value: "Unfurnished" },
];
const bhkOptions = [
  { label: "Select BHK", value: "" },
  { label: "1 BHK", value: "1" },
  { label: "2 BHK", value: "2" },
  { label: "3 BHK", value: "3" },
  { label: "4 BHK", value: "4" },
];

function AddNewUnit() {
  const navigate = useNavigate();
  const location = useLocation();
  const unitToEdit = location.state?.unit || {};

  const { jwt, user, isLoadingSession } = useAuth();
  // const { user } = useUser();

  const [formData, setFormData] = useState({
    buildingId: unitToEdit.building_id || "",
    unitName: unitToEdit.unit_name || "",
    unitArea: unitToEdit.unit_area || "",
    furnishingStatus: unitToEdit.furnishing_status || "",
    bhk: unitToEdit.bhk || "",
    rent: unitToEdit.rent || "",
    security_amount: unitToEdit.security_amount || "",
    noOfBathrooms: unitToEdit.no_of_bathrooms || 1,
    noOfBalconies: unitToEdit.no_of_balcony || 1,
    amenities: Array.isArray(unitToEdit.amenities) ? unitToEdit.amenities : [],
    status: unitToEdit.status || "active",
    occupancy_status: unitToEdit.occupancy_status || "vacant",
  });

  const [buildings, setBuildings] = useState([]);
  const [filesToUpload, setFilesToUpload] = useState([]); // Stores new File objects with their descriptions
  const [filePreviews, setFilePreviews] = useState([]); // Stores Object URLs for new file previews

  const [existingMedia, setExistingMedia] = useState(() => {
    // For media already on the server
    try {
      // Ensure unitToEdit.media is parsed if it's a string, and is an array.
      // Each item should have at least { name, url, location, description }
      const media = unitToEdit.media;
      if (media && typeof media === "string") {
        const parsed = JSON.parse(media);
        return Array.isArray(parsed)
          ? parsed.map((m) => ({ ...m, description: m.description || "" }))
          : [];
      }
      return Array.isArray(media)
        ? media.map((m) => ({ ...m, description: m.description || "" }))
        : [];
    } catch (e) {
      console.error("Error parsing existing media:", e);
      return [];
    }
  });
  const [mediaToDeleteFromFirebase, setMediaToDeleteFromFirebase] = useState(
    []
  ); // Firebase storage paths

  const [uploadProgress, setUploadProgress] = useState({}); // { fileIndexInUploadBatch: percentage }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const isEditMode = !!unitToEdit.entity_id;

  useEffect(() => {
    const fetchBuildingsData = async () => {
      /* ... (your existing fetch code) ... */
      if (!jwt) return;
      try {
        const buildingResponse = await APICall(
          "GET",
          paths.building.fetch,
          undefined,
          undefined,
          jwt
        );
        if (buildingResponse.status === "success") {
          setBuildings(buildingResponse.response || []);
        } else {
          console.error("Failed to fetch buildings:", buildingResponse.message);
          setError("Could not load buildings for selection.");
        }
      } catch (err) {
        console.error("Error fetching buildings:", err);
        setError("An error occurred while loading buildings.");
      }
    };
    fetchBuildingsData();
  }, [jwt]);

  // Cleanup object URLs for previews
  useEffect(() => {
    return () => {
      filePreviews.forEach(URL.revokeObjectURL);
    };
  }, [filePreviews]);

  const handleInputChange = (e) => {
    /* ... (your existing input change code) ... */
    const { name, value, type, checked } = e.target;
    if (type === "checkbox" && name === "amenities") {
      setFormData((prev) => ({
        ...prev,
        amenities: checked
          ? [...prev.amenities, value]
          : prev.amenities.filter((amenity) => amenity !== value),
      }));
    } else if (type === "number") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? "" : parseInt(value, 10),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileSelection = (selectedFileList) => {
    console.log("FileUpload sent:", selectedFileList); // Should be a FileList
    if (!selectedFileList || selectedFileList.length === 0) {
      // Optionally clear filesToUpload if an empty selection is made,
      // or just do nothing if you only want to append.
      // For now, let's assume new selections append.
      return;
    }
    // Expects a FileList
    const newFiles = Array.from(selectedFileList).map((file) => ({
      actualFile: file, // Store the original File object
      name: file.name, // For convenience, though actualFile.name is the source
      description: "", // Initialize description
      previewUrl: URL.createObjectURL(file), // Create preview URL immediately
    }));
    console.log("New files processed:", newFiles);
    setFilesToUpload((prevFiles) => [...prevFiles, ...newFiles]);
    // setFilePreviews((prevPreviews) => [
    //   ...prevPreviews,
    //   ...newFiles.map((f) => f.previewUrl),
    // ]);
  };

  const removeNewFile = (indexToRemove) => {
    const fileToRemove = filesToUpload[indexToRemove];
    if (fileToRemove && fileToRemove.previewUrl) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
    }
    setFilesToUpload((files) =>
      files.filter((_, index) => index !== indexToRemove)
    );
    setFilePreviews((previews) =>
      previews.filter((_, index) => {
        // This assumes previews are in same order as filesToUpload
        // A more robust way is to filter filesToUpload and then regenerate previews if they are out of sync.
        // For simplicity, direct filter is used if order is maintained.
        if (index === indexToRemove && fileToRemove.previewUrl) return false;
        return true;
      })
    );
    // Rebuild filePreviews from the updated filesToUpload to ensure sync
    setFilePreviews(
      filesToUpload
        .filter((_, index) => index !== indexToRemove)
        .map((f) => f.previewUrl)
    );
  };

  const updateNewFileDescription = (index, description) => {
    setFilesToUpload((files) =>
      files.map((file, i) => (i === index ? { ...file, description } : file))
    );
  };

  const removeExistingMediaItem = (indexToRemove) => {
    const itemToRemove = existingMedia[indexToRemove];
    if (itemToRemove && itemToRemove.location) {
      // Ensure 'location' (Firebase path) exists
      setMediaToDeleteFromFirebase((prev) => [...prev, itemToRemove.location]);
    }
    setExistingMedia((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const updateExistingMediaDescription = (index, description) => {
    setExistingMedia((media) =>
      media.map((item, i) => (i === index ? { ...item, description } : item))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setUploadProgress({});

    if (!formData.buildingId) {
      /* ... (validation) ... */ setError("Please select a building.");
      setIsLoading(false);
      return;
    }
    const selectedBuilding = buildings.find(
      (b) => b.entity_id === formData.buildingId
    );
    if (!selectedBuilding) {
      /* ... (validation) ... */ setError("Selected building not found.");
      setIsLoading(false);
      return;
    }

    let unitPayload = {
      /* ... (construct unitPayload as before) ... */
      building_id: selectedBuilding.entity_id,
      unit_name: formData.unitName,
      unit_area: formData.unitArea,
      furnishing_status: formData.furnishingStatus,
      bhk: formData.bhk,
      no_of_bathrooms: formData.noOfBathrooms,
      no_of_balcony: formData.noOfBalconies,
      amenities: formData.amenities,
      status: formData.status,
      occupancy_status: formData.occupancy_status,
      rent: formData.rent,
      security_amount: formData.security_amount,
      building_name: selectedBuilding.building_name || "",
      address: selectedBuilding.address || "",
    };
    if (isEditMode && unitToEdit.entity_id)
      unitPayload.unit_id = unitToEdit.entity_id;

    try {
      let unitId = unitToEdit.entity_id;
      let operationSuccessMessage = "";

      // Step 0: Save/Update unit text details (API call without media first)
      const unitDetailsPayload = { ...unitPayload }; // Exclude media for this initial call
      // delete unitDetailsPayload.media; // If your payload might have a media field from previous attempts

      if (isEditMode) {
        const updateRes = await APICall(
          "PUT",
          paths.unit.update,
          unitDetailsPayload,
          undefined,
          jwt
        );
        if (updateRes.status !== "success")
          throw new Error(
            updateRes.message || "Failed to update unit details."
          );
        operationSuccessMessage = "Unit details updated successfully!";
      } else {
        const addRes = await APICall(
          "POST",
          paths.unit.add,
          unitDetailsPayload,
          undefined,
          jwt
        );
        if (addRes.status !== "success" || !addRes.response?.entity_id)
          throw new Error(
            addRes.message || "Failed to add unit or get unit ID."
          );
        unitId = addRes.response.entity_id;
        operationSuccessMessage = "Unit added successfully!";
        if (selectedBuilding?.entity_id) {
          /* Update building count */
          await APICall(
            "PUT",
            paths.building.updateCount,
            {
              building_id: selectedBuilding.entity_id,
              no_of_units:
                (parseInt(selectedBuilding.no_of_units, 10) || 0) + 1,
            },
            undefined,
            jwt
          );
        }
      }

      // Step 1: Delete marked existing files from Firebase Storage
      if (mediaToDeleteFromFirebase.length > 0) {
        operationSuccessMessage += ` Processing ${mediaToDeleteFromFirebase.length} deletions...`;
        const deletePromises = mediaToDeleteFromFirebase.map((locationPath) => {
          const fileRef = ref(storage, locationPath);
          return deleteObject(fileRef).catch((err) => {
            console.warn(
              `Failed to delete ${locationPath} from Firebase:`,
              err.message
            );
            // Decide if you want to append this to main error or just log
          });
        });
        await Promise.all(deletePromises);
        setMediaToDeleteFromFirebase([]); // Clear the queue
        operationSuccessMessage = operationSuccessMessage.replace(
          ` Processing ${mediaToDeleteFromFirebase.length} deletions...`,
          " Old images processed for deletion."
        );
      }

      // Step 2: Upload new files to Firebase
      const newlyUploadedMediaObjects = [];
      if (filesToUpload.length > 0 && unitId) {
        operationSuccessMessage += ` Uploading ${filesToUpload.length} new image(s)...`;

        for (let i = 0; i < filesToUpload.length; i++) {
          const fileToUpload = filesToUpload[i]; // This is an object { actualFile, name, description, previewUrl }
          const actualFile = fileToUpload.actualFile;

          setUploadProgress((prev) => ({ ...prev, [i]: 0 })); // Initialize progress for this file

          const baseUploadLocation = GetUploadMediaBaseLocation(
            cloudStorageBucket.units,
            user
          );
          // Use a unique name for storage, but keep original name for metadata
          const uniqueFileNameInStorage = `${Date.now()}_${actualFile.name}`;
          const firebasePath = `${baseUploadLocation}${unitId}/${uniqueFileNameInStorage}`;
          const storageRef = ref(storage, firebasePath);
          const uploadTask = uploadBytesResumable(storageRef, actualFile);

          try {
            await new Promise((resolve, reject) => {
              uploadTask.on(
                "state_changed",
                (snapshot) => {
                  const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  setUploadProgress((prev) => ({ ...prev, [i]: progress }));
                },
                (uploadError) => {
                  console.error(
                    `Upload failed for ${actualFile.name}:`,
                    uploadError
                  );
                  // Don't reject, let other uploads continue. Collect error.
                  setError(
                    (prev) => prev + `Upload error for ${actualFile.name}. `
                  );
                  resolve(); // Allow loop to continue
                },
                async () => {
                  try {
                    const downloadURL = await getDownloadURL(
                      uploadTask.snapshot.ref
                    );
                    newlyUploadedMediaObjects.push({
                      name: actualFile.name, // Original name
                      location: firebasePath, // Path in Firebase
                      url: downloadURL,
                      type: actualFile.type,
                      size: actualFile.size,
                      description: fileToUpload.description || "",
                      // height, width could be added if captured
                    });
                    resolve();
                  } catch (getUrlError) {
                    console.error(
                      `Get URL failed for ${actualFile.name}:`,
                      getUrlError
                    );
                    setError(
                      (prev) =>
                        prev + `URL fetch error for ${actualFile.name}. `
                    );
                    resolve(); // Allow loop to continue
                  }
                }
              );
            });
          } catch (individualUploadError) {
            // This catch is if the promise wrapping uploadTask itself fails, though unlikely with current setup
            console.error(
              "An error occurred during the upload promise wrapper:",
              individualUploadError
            );
          }
        }
        operationSuccessMessage = operationSuccessMessage.replace(
          ` Uploading ${filesToUpload.length} new image(s)...`,
          ` ${newlyUploadedMediaObjects.length} of ${filesToUpload.length} new image(s) processed.`
        );
      }

      // Step 3: Final Media List Construction for Backend
      // Combine remaining existing media with newly uploaded media
      const finalMediaList = [...existingMedia, ...newlyUploadedMediaObjects];

      // Step 4: Update Backend with the final media list
      // This API call should always happen if unitId exists, to sync media state.
      // Backend should be able to handle an empty array for media.
      if (unitId) {
        const mediaPayloadForBackend = {
          unit_id: unitId,
          media: JSON.stringify(finalMediaList), // Send empty array if no media
        };
        operationSuccessMessage += " Syncing media list with server...";
        const mediaUpdateRes = await APICall(
          "PUT",
          paths.unit.update,
          mediaPayloadForBackend,
          undefined,
          jwt
        );
        if (mediaUpdateRes.status !== "success") {
          throw new Error(
            mediaUpdateRes.message ||
              "Failed to update unit with final media list."
          );
        }
        operationSuccessMessage = operationSuccessMessage.replace(
          " Syncing media list with server...",
          " Media list synced."
        );
      }

      alert(operationSuccessMessage.trim());
      // my-properties/:unitId
      // navigate("/dashboard/units");
      navigate(`/dashboard/my-properties/${unitId}`, {
        state: { unit: { ...unitPayload, entity_id: unitId } },
      });
    } catch (err) {
      console.error("Overall operation failed:", err);
      setError(
        (prevError) =>
          (prevError ? prevError + " | " : "") +
          (err.message || "An unexpected error occurred.")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const calculateOverallProgress = () => {
    const progressValues = Object.values(uploadProgress);
    if (progressValues.length === 0) return 0;
    const total = progressValues.reduce((sum, current) => sum + current, 0);
    return total / progressValues.length;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        {isEditMode ? "Edit Unit" : "Add New Unit"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">
            Unit Information
          </h2>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unit Images (Optional)
            </label>
            <FileUpload onFileUpload={handleFileSelection} />{" "}
            {/* Pass FileList here */}
            {isLoading &&
              filesToUpload.length > 0 &&
              calculateOverallProgress() > 0 && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{ width: `${calculateOverallProgress()}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600 text-center mt-1">
                    Overall Upload Progress:{" "}
                    {Math.round(calculateOverallProgress())}%
                  </p>
                </div>
              )}
            {/* Display Previews for New Files to Upload */}
            {filesToUpload.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  New Images to Upload:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filesToUpload.map((file, index) => (
                    <div
                      key={`new-${index}-${file.name}`}
                      className="relative border p-2 rounded-md"
                    >
                      <img
                        src={file.previewUrl}
                        alt={file.name}
                        className="h-32 w-full object-cover rounded-md mb-2"
                      />
                      <input
                        type="text"
                        placeholder="Description"
                        value={file.description}
                        onChange={(e) =>
                          updateNewFileDescription(index, e.target.value)
                        }
                        className="w-full p-1 border rounded-md text-xs mb-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewFile(index)}
                        className="absolute top-1 right-1 bg-red-600 text-white p-0.5 rounded-full leading-none hover:bg-red-700"
                        aria-label="Remove new image"
                      >
                        <FaTrashAlt size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Display Existing Media */}
            {existingMedia.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Current Images:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {existingMedia.map((media, index) => (
                    <div
                      key={media.location || `existing-${index}`}
                      className="relative border p-2 rounded-md"
                    >
                      {media.url ? (
                        <img
                          src={media.url}
                          alt={media.name || "Existing media"}
                          className="h-32 w-full object-cover rounded-md mb-2"
                        />
                      ) : media.location ? ( // Fallback if only location is present
                        <FileDownload
                          filePath={media.location}
                          className="h-32 w-full object-cover rounded-md mb-2"
                        />
                      ) : (
                        <div className="h-32 w-full flex items-center justify-center bg-gray-100 text-gray-400 rounded-md mb-2">
                          No Preview
                        </div>
                      )}
                      <input
                        type="text"
                        placeholder="Description"
                        value={media.description}
                        onChange={(e) =>
                          updateExistingMediaDescription(index, e.target.value)
                        }
                        className="w-full p-1 border rounded-md text-xs mb-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingMediaItem(index)}
                        className="absolute top-1 right-1 bg-red-600 text-white p-0.5 rounded-full leading-none hover:bg-red-700"
                        aria-label="Remove existing image"
                      >
                        <FaTrashAlt size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* ... (rest of your form inputs for building, unitName, etc.) ... */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {" "}
            <FormSelect
              label="Select Building"
              id="buildingId"
              name="buildingId"
              value={formData.buildingId}
              onChange={handleInputChange}
              required
              error={!formData.buildingId && "Building selection is required"}
            >
              {" "}
              <option value="">-- Select a Building --</option>{" "}
              {buildings.map((building) => (
                <option key={building.entity_id} value={building.entity_id}>
                  {" "}
                  {building.building_name} ({building.address}){" "}
                </option>
              ))}{" "}
            </FormSelect>{" "}
            <FormInput
              label="Unit Name / Number"
              id="unitName"
              name="unitName"
              placeholder="e.g., A-101, Flat 5B"
              value={formData.unitName}
              onChange={handleInputChange}
              required
              error={!formData.unitName && "Unit name is required"}
            />{" "}
            <FormInput
              label="Unit Area (sq.ft)"
              id="unitArea"
              name="unitArea"
              type="number"
              placeholder="e.g., 1200"
              value={formData.unitArea}
              onChange={handleInputChange}
            />{" "}
            <FormSelect
              label="Furnishing Status"
              id="furnishingStatus"
              name="furnishingStatus"
              value={formData.furnishingStatus}
              onChange={handleInputChange}
            >
              {" "}
              {furnishingItems.map((item) => (
                <option key={item.value} value={item.value}>
                  {" "}
                  {item.label}{" "}
                </option>
              ))}{" "}
            </FormSelect>{" "}
            <FormSelect
              label="BHK Type"
              id="bhk"
              name="bhk"
              value={formData.bhk}
              onChange={handleInputChange}
            >
              {" "}
              {bhkOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {" "}
                  {item.label}{" "}
                </option>
              ))}{" "}
            </FormSelect>{" "}
          </div>
          <div className="mt-4 space-y-1">
            {" "}
            <NumberStepper
              label="Number of Bathrooms"
              name="noOfBathrooms"
              value={formData.noOfBathrooms}
              onChange={handleInputChange}
              onIncrement={() =>
                setFormData((prev) => ({
                  ...prev,
                  noOfBathrooms: Math.min(10, (prev.noOfBathrooms || 0) + 1),
                }))
              }
              onDecrement={() =>
                setFormData((prev) => ({
                  ...prev,
                  noOfBathrooms: Math.max(0, (prev.noOfBathrooms || 0) - 1),
                }))
              }
              min={0}
            />{" "}
            <NumberStepper
              label="Number of Balconies"
              name="noOfBalconies"
              value={formData.noOfBalconies}
              onChange={handleInputChange}
              onIncrement={() =>
                setFormData((prev) => ({
                  ...prev,
                  noOfBalconies: Math.min(10, (prev.noOfBalconies || 0) + 1),
                }))
              }
              onDecrement={() =>
                setFormData((prev) => ({
                  ...prev,
                  noOfBalconies: Math.max(0, (prev.noOfBalconies || 0) - 1),
                }))
              }
              min={0}
            />{" "}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          {" "}
          {/* Amenities Section */}
          <h2 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">
            Amenities
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3">
            {amenitiesList.map((amenity) => (
              <div key={amenity} className="flex items-center">
                {" "}
                <input
                  type="checkbox"
                  id={`amenity-${amenity.replace(/\s+/g, "-")}`}
                  name="amenities"
                  value={amenity}
                  checked={formData.amenities.includes(amenity)}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />{" "}
                <label
                  htmlFor={`amenity-${amenity.replace(/\s+/g, "-")}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {" "}
                  {amenity}{" "}
                </label>{" "}
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: Status (was commented out, keeping it commented) */}
        {/* <div className="bg-white p-6 rounded-lg shadow-md"> ... </div> */}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
            {error}
          </p>
        )}

        <div className="flex justify-end pt-4">
          {" "}
          {/* Action Buttons */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="bg-gray-200 text-gray-700 hover:bg-gray-300 px-6 py-2.5 rounded-md text-sm font-medium mr-3 transition-colors"
          >
            {" "}
            Cancel{" "}
          </button>
          <button
            type="submit"
            disabled={isLoading || !formData.unitName || !formData.buildingId}
            className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-2.5 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {isLoading && (
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                {" "}
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>{" "}
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>{" "}
              </svg>
            )}
            {isLoading
              ? filesToUpload.length > 0 && calculateOverallProgress() > 0
                ? `Uploading (${Math.round(calculateOverallProgress())}%)...`
                : "Processing..."
              : isEditMode
                ? "Update Unit"
                : "Add Unit"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddNewUnit;
